import React, { useEffect, useMemo, useState } from 'react';

import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import { resolveEventEntityName } from '../../util/listingSeoCopy';
import {
  relatedShowListingsParams,
  fetchRelatedShowListings,
} from '../../util/relatedShowListings';

import css from './ListingPage.module.css';

const lookupCache = new Map();

const clean = value => String(value || '').trim();

/**
 * Crawlable “More {Event} Tickets” module — other upcoming dates of the same show.
 *
 * The payload is fetched during loadData (SSR) and passed as `ssrPayload`, so the
 * section is in the first HTML and does not insert after paint (CLS). The client
 * fetch below only runs as a fallback when the SSR fetch failed.
 */
const SectionRelatedShowListings = ({ listingId, title, publicData, ssrPayload }) => {
  const eventLabel = useMemo(
    () => resolveEventEntityName({ title, publicData }),
    [title, publicData]
  );

  const params = useMemo(
    () => relatedShowListingsParams({ listingId, title, publicData }),
    [listingId, title, publicData]
  );

  const cacheKey = params ? JSON.stringify(params) : null;
  const [fallbackPayload, setFallbackPayload] = useState(() =>
    cacheKey ? lookupCache.get(cacheKey) || null : null
  );

  const payload = ssrPayload || fallbackPayload;

  useEffect(() => {
    // SSR (or a previous fallback fetch) already provided the data.
    if (ssrPayload || !cacheKey) {
      return;
    }
    if (lookupCache.has(cacheKey)) {
      setFallbackPayload(lookupCache.get(cacheKey));
      return;
    }

    let cancelled = false;
    fetchRelatedShowListings(params).then(body => {
      const next = body || { eventName: eventLabel, events: [] };
      lookupCache.set(cacheKey, next);
      if (!cancelled) {
        setFallbackPayload(next);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ssrPayload, cacheKey, params, eventLabel]);

  const events = payload?.events || [];
  if (!events.length) {
    return null;
  }

  const name = clean(payload?.eventName) || eventLabel;

  return (
    <div className={css.sectionRelatedShowListings}>
      <SeoUpcomingEvents
        title={`More ${name} Tickets`}
        intro={`Other upcoming ${name} dates and performances`}
        events={events}
        emptyMessage=""
        venueName={name}
      />
    </div>
  );
};

export default SectionRelatedShowListings;
